<template>
  <div :class="['v-date-picker']">
    <vc-date-picker
      class="vc-date-picker"
      v-model="selectedDate"
      :masks="masks"
      :theme="theme"
      :model-config="modelConfig"
      @input="emitInputEvent"
      :locale="locale"
      :min-date="minDate"
      :max-date="maxDate"
      :popover="{ visibility: 'focus' }"
      :mode="dateTime ? 'dateTime' : 'date'"
      trim-weeks
      is24hr
    >
      <v-input
        ref="date"
        slot-scope="{ inputValue, inputEvents }"
        :value="inputValue"
        v-on="inputEvents"
        v-bind="[$attrs]"
        :invalid="isInvalid"
        :small="small"
        @focus="hasFocus = true"
        @blur="hasFocus = false"
        :autocomplete="$attrs.autocomplete || 'off'"
        @invalid="isInvalid = true"
      >
        <slot></slot>
      </v-input>
      <template slot="header-left-button">
        <v-icon name="arrow-key-left" size="xsmall" />
      </template>
      <template slot="header-right-button">
        <v-icon name="arrow-key-right" size="xsmall" />
      </template>
    </vc-date-picker>
    <div
      :class="[
        'v-date-picker__icon',
        hasFocus && 'v-date-picker__icon--focus',
        small && 'v-date-picker__icon--small'
      ]"
      aria-hidden="true"
    >
      <v-icon name="calendar-month" size="xsmall" />
    </div>
    <input
      v-if="isoDateName"
      type="hidden"
      :name="isoDateName"
      :value="selectedDate"
    />
  </div>
</template>

<script>
import * as moment from "moment";
import DatePicker from "v-calendar/lib/components/date-picker.umd";
import conf from "./conf";

export default {
  inheritAttrs: false,
  components: {
    "vc-date-picker": DatePicker
  },
  props: {
    small: {
      type: Boolean,
      default: false
    },
    date: {
      type: [Number, String, Date],
      default: null
    },
    min: {
      type: [Number, String],
      default: null
    },
    max: {
      type: [Number, String],
      default: null
    },
    future: {
      type: Boolean,
      default: false
    },
    past: {
      type: Boolean,
      default: false
    },
    pastFourteen: {
      type: Boolean,
      default: false
    },
    dateTime: {
      type: Boolean,
      default: false
    },
    isoDateName: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      hasFocus: false,
      selectedDate: this.initSelectedDate(this.$props.date),
      masks: conf.masks,
      theme: conf.theme,
      modelConfig: conf.modelConfig,
      isInvalid: false
    };
  },
  watch: {
    date(date) {
      this.selectedDate = date;
    }
  },
  computed: {
    minDate() {
      if (this.pastFourteen) {
        let date = new Date();
        return date.setDate(date.getDate() - 14);
      }
      if (this.future) return new Date();
      return this.min;
    },
    maxDate() {
      if (this.past) return new Date();
      return this.max;
    },
    locale() {
      const locale =
        (!!window.CLIENT && window.CLIENT.locale) || window.navigator.language;
      return locale || "da";
    }
  },
  methods: {
    emitInputEvent(date) {
      if (this.$refs.date && this.$refs.date.showError && this.selectedDate) {
        this.isInvalid = false;
      }
      this.$emit("input", { target: { value: date } });
    },
    parseDate(date) {
      if (!date) return null;
      // Make sure Epoch / Unix is handled as number
      const normalized = /^\d+$/.test(date) ? Number(date) : date;
      return moment(normalized).toISOString();
    },
    initSelectedDate(date) {
      if (!date) return null;
      return this.parseDate(date);
    }
  }
};
</script>

<style scoped lang="scss">
@import "../../sass/component.scss";

.v-date-picker::v-deep {
  position: relative;

  .v-date-picker__icon {
    pointer-events: none;
    position: absolute;
    top: 15px;
    right: 15px;
    color: theme-color("placeholder");

    &.v-date-picker__icon--small {
      top: 8px;
      right: 8px;
      .v-icon {
        width: 20px;
      }
    }

    &.v-date-picker__icon--focus {
      svg path {
        fill: theme-color("focus");
      }
    }
  }

  .vc-popover-content-wrapper {
    width: 100%;
    z-index: 2000;

    @include media-breakpoint-up(sm) {
      width: auto;
    }
  }

  .vc-popover-caret {
    display: none;
  }

  .vc-title {
    color: theme-color("dark");
    text-transform: uppercase;
    font-weight: bold;
    font-size: 1em;
    padding-top: 2px;
  }

  .vc-container {
    padding-top: 5px;
    background-color: white;
    border-radius: 4px;
    line-height: normal;
    width: 100%;
    font-size: 0.9rem;

    @include media-breakpoint-up(sm) {
      width: 400px;
      font-size: 1rem;
    }

    &.vc-reset * {
      line-height: normal;
    }

    .vc-header {
      padding-bottom: 12px;
    }

    .vc-grid-container {
      -webkit-overflow-scrolling: auto;
    }

    .vc-arrows-container {
      padding: 4px 11px;
    }

    .vc-weekday {
      text-transform: uppercase;
      color: theme-color("gray-dark");
      font-size: 0.75em;
    }

    .vc-day {
      padding: 5px 0;
      font-size: 0.875em;

      .vc-highlight,
      .vc-day-content {
        width: 28px;
        height: 28px;

        @include media-breakpoint-up(xs) {
          width: 34px;
          height: 34px;
        }
      }

      .vc-highlight {
        background-color: theme-color("secondary");
      }

      .vc-day-content {
        border-radius: 50%;
        cursor: pointer;

        &:hover {
          color: white;
          background-color: theme-color("secondary");
          font-weight: bold;
        }

        &:focus {
          background-color: transparent;
        }
      }

      &.is-today {
        .vc-day-content {
          background-color: theme-color("gray");

          &:hover {
            background-color: theme-color("secondary");
          }
        }

        .vc-highlights + .vc-day-content {
          background-color: transparent;
        }
      }

      .day-not-in-month {
        color: #b4b4b4;
      }

      .vc-day-content.vc-day-disabled {
        color: #b4b4b4;

        &:hover {
          font-weight: normal;
          background-color: white;
          cursor: auto;
        }
      }
    }

    .vc-date {
      padding: 0;

      > * {
        font-size: 1em;
      }
    }
  }

  .vc-title-wrapper {
    position: static;

    .vc-popover-content-wrapper {
      transform: none !important;
      right: 0px;
    }

    .vc-popover-content {
      background-color: white;
      margin-top: 5px;
      margin-bottom: 10px;
    }

    .vc-nav-title {
      color: theme-color("dark");
      font-weight: bold;
      font-size: 1em;
    }

    .vc-nav-arrow {
      svg {
        padding: 10px;
      }

      &.vc-mr-auto {
        margin-left: 15px;
      }

      &.vc-ml-auto {
        margin-right: 15px;
      }
    }

    .vc-nav-cell {
      display: flex;
      justify-content: center;
      flex-direction: column;
      text-align: center;
      text-transform: uppercase;
      font-size: 0.875em;
      width: 60px;
      height: 60px;
      border-radius: 50%;
      margin-top: 15px;

      &:hover {
        color: white;
        font-weight: bold;
        background-color: theme-color("secondary");
      }

      &.vc-nav-cell-active {
        background-color: theme-color("secondary");
        color: white;
        font-weight: bold;
      }

      &.vc-nav-cell-inactive-current {
        background-color: theme-color("gray");
      }
    }
  }
}
</style>
